// rsaPathTypes.js — Data model types, constants, and factory functions for the
// multi-path / multi-lane system. No React imports — fully pure.

// ─── ISD case constants ─────────────────────────────────────────────────────

export const ISD_CASES = {
  LEFT_TURN: "left-turn",
  RIGHT_TURN: "right-turn",
  F_CASE: "f-case",
  // Case F WITH an opposing vehicle placed in the opposite left-turn lane. An
  // explicit choice (2026-09-06): the opposite point is MANDATORY for it — the
  // analysis' object pass is gated on that point, so without it the run would
  // silently be a plain F. Exported to the XML as case "F" + <OppositePoint> +
  // <OppositeObject/>.
  F_CASE_OBJECT: "f-case-object",
  PEDESTRIAN: "pedestrian",
};

export const ISD_CASE_LABELS = {
  [ISD_CASES.LEFT_TURN]: "B1 (Left Turn)",
  [ISD_CASES.RIGHT_TURN]: "B2 (Right Turn)",
  [ISD_CASES.F_CASE]: "F (Left turn from major road)",
  [ISD_CASES.F_CASE_OBJECT]: "F + Opposing vehicle (Left turn from major road)",
  [ISD_CASES.PEDESTRIAN]: "PD (Pedestrian)",
};

export const isObjectCase = (isdCase) => isdCase === ISD_CASES.F_CASE_OBJECT;

// Opposing-vehicle object defaults (feet). Width/height match the legacy config's
// OppositeCarWidth/OppositeCarHeight; the analysis inserts a cylinder (the legacy
// mesh shape) unless "car" is chosen; count > 1 queues vehicles `gap` apart.
export const OPPOSITE_OBJECT_SHAPES = { CYLINDER: "cylinder", CAR: "car" };
export const OPPOSITE_OBJECT_DEFAULTS = {
  shape: OPPOSITE_OBJECT_SHAPES.CYLINDER,
  width: 6.67,
  height: 6.33,
  length: 15,
  count: 1,
  gap: 6,
};

/** Effective object params for an intersection (defaults filled in). */
export function resolveOppositeObject(oppositeObject) {
  const o = { ...OPPOSITE_OBJECT_DEFAULTS, ...(oppositeObject || {}) };
  const num = (v, d) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : d);
  return {
    shape:
      o.shape === OPPOSITE_OBJECT_SHAPES.CAR
        ? OPPOSITE_OBJECT_SHAPES.CAR
        : OPPOSITE_OBJECT_SHAPES.CYLINDER,
    width: num(o.width, OPPOSITE_OBJECT_DEFAULTS.width),
    height: num(o.height, OPPOSITE_OBJECT_DEFAULTS.height),
    length: num(o.length, OPPOSITE_OBJECT_DEFAULTS.length),
    count: Math.max(1, Math.round(num(o.count, 1))),
    gap: Number.isFinite(Number(o.gap)) && Number(o.gap) >= 0 ? Number(o.gap) : OPPOSITE_OBJECT_DEFAULTS.gap,
  };
}

/** Object-case intersections that still lack their mandatory opposite point. */
export function findObjectCasesMissingPoint(intersections) {
  return (intersections || []).filter(
    (i) =>
      isObjectCase(i?.isdCase) &&
      !(
        Number.isFinite(i?.oppositePoint?.insertionPoint?.lat) &&
        Number.isFinite(i?.oppositePoint?.insertionPoint?.lng)
      ),
  );
}

// ─── Lane type constants ─────────────────────────────────────────────────────

export const LANE_TYPES = {
  CENTER: "center",
  RIGHT_SHOULDER: "right-shoulder",
  LEFT_SHOULDER: "left-shoulder",
  CUSTOM: "custom",
};

export const LANE_TYPE_LABELS = {
  [LANE_TYPES.CENTER]: "Center Lane",
  [LANE_TYPES.RIGHT_SHOULDER]: "Right Shoulder",
  [LANE_TYPES.LEFT_SHOULDER]: "Left Shoulder",
  [LANE_TYPES.CUSTOM]: "Custom",
};

// ─── Factory functions ────────────────────────────────────────────────────────

/**
 * Create a new RsaPath object with sane defaults.
 * @param {Partial<RsaPath>} overrides
 * @returns {RsaPath}
 */
export function createPath(overrides = {}) {
  return {
    id: crypto.randomUUID(),
    label: "Route",
    waypoints: [],
    pathData: null,
    colorIndex: 0,
    visible: true,
    ...overrides,
  };
}

/**
 * Create a new RsaLane object linked to a parent path.
 *
 * Waypoint shape: each entry in `waypoints` is `{ lat, lng }`.
 * Waypoints may optionally carry `bridged: true` to indicate the point was
 * placed via bridge mode (used for renderer tint only; ignored on export).
 * Waypoints may also optionally carry `ai_anchor: true` to indicate the point
 * was suggested by Claude vision during the auto-trace AI-assist round
 * (used for renderer tint only; ignored on export).
 * Waypoints may also optionally carry `gap_after: true` to indicate the
 * auto-trace stopped at a gap right after this point (durable record of the
 * gap-continue flow; renderer-only, ignored on export).
 *
 * @param {string} pathId
 * @param {Partial<RsaLane>} overrides
 * @returns {RsaLane}
 */
export function createLane(pathId, overrides = {}) {
  return {
    id: crypto.randomUUID(),
    pathId,
    laneType: LANE_TYPES.CUSTOM,
    label: "Lane",
    waypoints: [],
    pathData: null,
    colorIndex: 1,
    visible: true,
    creationMode: "manual", // 'offset' | 'manual'
    sourcePathId: null,
    side: null, // 'left' | 'right'
    offsetFeet: null,
    directionMode: null, // 'same' | 'reverse'
    lanePosition: "LL", // 'LL' | 'CL' | 'RL'
    direction: "P", // 'N' | 'P'
    speedSegments: [], // [{ mileMark: number, speedMph: number }] — breakpoints along the lane
    reviewed: false, // user clicked "Done" on step 3 — persisted so the ready state survives refresh/tab switch
    ...overrides,
  };
}

/**
 * Create a new ISD intersection point.
 * @param {Partial<RsaIntersection>} overrides
 * @returns {RsaIntersection}
 */
export function createIntersection(overrides = {}) {
  return {
    id: crypto.randomUUID(),
    intersectionName: "", // INTERSECTION — name/number of intersection
    maneuverIndex: 1, // ID — ISD maneuver ID (auto-incremented)
    isdCase: ISD_CASES.LEFT_TURN, // CASE — left turn, right turn, F case
    oppositePoint: null, // OPPOSITEP { insertionPoint: {lat, lng}, azimuth, arrowLengthFeet } — F-with-object case (cyan block)
    oppositeObject: null, // { shape, width, height, length, count, gap } (feet) — F-with-object case; null = defaults
    rsaPathId: null, // RSAPATH — references a path.id
    secondaryRoute: "", // Secondary route — minor road name (reports)
    insertionPoint: null, // {lat, lng} from map click
    azimuth: 0, // degrees clockwise from north
    arrowLengthFeet: 20, // length of azimuth arrow, set by drag
    visible: true,
    ...overrides,
  };
}

/**
 * Initial reducer state.
 * @returns {RsaPathsState}
 */
export function createInitialState() {
  return {
    paths: [],
    lanes: [],
    intersections: [],
    xmlToJsonMap: {},
    // Per-project LRS state override ('AL' | 'MA' | ...). null means "let
    // lrsGate auto-detect from this project's geometry".
    lrsState: null,
    activePathId: null,
    activeLaneId: null,
    activeIntersectionId: null,
    activeEntityType: "path", // 'path' | 'lane'
    laneEditMode: false,
    speedEditMode: false,
    selectedLaneWaypointIndices: [],
  };
}

/**
 * Where the opposing vehicle sits relative to the ISD point along its lane.
 *
 * The ISD analysis puts the EYE at the ISD point and runs the oncoming target along
 * the lane from the lane's FIRST point toward the ISD point (the exported <PATH>
 * order — the same order the lane is stored in, for both P and N). So the opposing
 * vehicle must lie on the lane BETWEEN the lane start and the ISD point ("upstream");
 * a block past the ISD point ("downstream") is behind the camera and can never
 * occlude anything.
 *
 * Returns "upstream" | "downstream" | null (no lane geometry / no points).
 */
export function oppositeBlockSide(intersection, lanes) {
  const isd = intersection?.insertionPoint;
  const opp = intersection?.oppositePoint?.insertionPoint;
  if (!isd || !opp) return null;
  const lane = (lanes || []).find((l) => l.id === intersection.rsaPathId);
  const coords = lane?.pathData?.geometry?.coordinates; // [lng, lat][]
  if (!Array.isArray(coords) || coords.length < 2) return null;
  // Arc position (in coordinate-index space with a fractional projection onto the
  // nearest segment) — enough to order two points along the same lane.
  const arcOf = (pt) => {
    let best = null;
    for (let i = 0; i < coords.length - 1; i += 1) {
      const [ax, ay] = coords[i];
      const [bx, by] = coords[i + 1];
      const vx = bx - ax;
      const vy = by - ay;
      const len2 = vx * vx + vy * vy || 1e-18;
      let t = ((pt.lng - ax) * vx + (pt.lat - ay) * vy) / len2;
      t = Math.max(0, Math.min(1, t));
      const px = ax + t * vx;
      const py = ay + t * vy;
      const d2 = (pt.lng - px) ** 2 + (pt.lat - py) ** 2;
      if (best === null || d2 < best.d2) best = { d2, arc: i + t };
    }
    return best ? best.arc : null;
  };
  const a = arcOf(isd);
  const b = arcOf(opp);
  if (a === null || b === null) return null;
  return b <= a ? "upstream" : "downstream";
}
